import React from 'react';
import {
  useNotifications,
  useNotificationActions
} from '@/shared/stores/notification-store';
import type {
  Notification,
  NotificationType
} from '@/shared/types/notification';

/**
 * Получить CSS классы для типа уведомления
 * @param type - Тип уведомления
 * @returns CSS классы DaisyUI
 */
const getAlertClasses = (type: NotificationType): string => {
  const baseClasses = 'alert shadow-lg';

  switch (type) {
    case 'success':
      return `${baseClasses} alert-success`;
    case 'error':
      return `${baseClasses} alert-error`;
    case 'warning':
      return `${baseClasses} alert-warning`;
    case 'info':
      return `${baseClasses} alert-info`;
    default:
      return `${baseClasses} alert-info`;
  }
};

/**
 * Получить иконку для типа уведомления
 * @param type - Тип уведомления
 * @returns CSS класс иконки FontAwesome
 */
const getIcon = (type: NotificationType): string => {
  switch (type) {
    case 'success':
      return 'fas fa-check-circle';
    case 'error':
      return 'fas fa-exclamation-circle';
    case 'warning':
      return 'fas fa-exclamation-triangle';
    case 'info':
      return 'fas fa-info-circle';
    default:
      return 'fas fa-info-circle';
  }
};

/**
 * Пропсы для компонента NotificationItem
 */
interface NotificationItemProps {
  notification: Notification;
  onDismiss: (id: string) => void;
}

/**
 * Компонент отдельного уведомления
 * @param notification - Данные уведомления
 * @param onDismiss - Функция закрытия уведомления
 * @returns JSX элемент
 */
const NotificationItem: React.FC<NotificationItemProps> = ({
  notification,
  onDismiss
}) => {
  const alertClasses = getAlertClasses(notification.type);
  const iconClass = getIcon(notification.type);

  return (
    <div
      className={`${alertClasses} notification-item flex gap-2`}
      role="alert"
      aria-live="polite"
      style={{
        animation: 'slideInFromRight 0.2s ease-out'
      }}
    >
      <div className="flex items-center gap-2 flex-1">
        <i
          className={`${iconClass} text-lg flex-shrink-0`}
          aria-hidden="true"
        ></i>
        <div className="flex-1 min-w-0">
          {notification.title && (
            <div className="font-semibold text-sm mb-1">
              {notification.title}
            </div>
          )}
          <div className="text-sm break-words flex-1">
            {notification.message}
          </div>
        </div>
      </div>

      {notification.dismissible && (
        <button
          onClick={() => onDismiss(notification.id)}
          className="btn btn-sm btn-ghost btn-circle flex-shrink-0 ml-2"
          aria-label="Закрыть уведомление"
          title="Закрыть"
        >
          <i className="fas fa-times text-sm"></i>
        </button>
      )}
    </div>
  );
};

/**
 * Контейнер для отображения уведомлений
 * Отображает snackbar уведомления в фиксированной позиции
 * @returns JSX элемент
 */
export const NotificationContainer: React.FC = () => {
  const notifications = useNotifications();
  const { removeNotification } = useNotificationActions();

  if (notifications.length === 0) {
    return null;
  }

  return (
    <>
      {/* CSS стили для анимаций */}
      <style>{`
        @keyframes slideInFromRight {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        
        .notification-item {
          transition: all 0.2s ease-out;
        }
        
        .notification-item:hover {
          transform: translateX(-4px);
        }
      `}</style>

      {/* Контейнер уведомлений */}
      <div
        className="fixed top-4 right-4 z-50 flex flex-col gap-2 max-w-sm w-full"
        style={{ zIndex: 9999 }}
      >
        {notifications.map((notification: Notification) => (
          <NotificationItem
            key={notification.id}
            notification={notification}
            onDismiss={removeNotification}
          />
        ))}
      </div>
    </>
  );
};
